import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import SearchIcon from '@mui/icons-material/Search';
import * as XLSX from 'xlsx';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { IconButton } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { Link } from 'react-router-dom'; // Import Link from react-router-dom
import Button from '@mui/material/Button';
import RefreshIcon from '@mui/icons-material/Refresh';
import FeedIcon from '@mui/icons-material/Feed';
import Select from 'react-select'; // Import react-select
import CopyrightIcon from '@mui/icons-material/Copyright';
import CircularProgress from '@mui/material/CircularProgress';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

function Stock() {
    const [dataBill, setDataBill] = useState([]);
    const [dataItem, setDataItem] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);
    const tableRef = React.createRef();
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [selectedOption, setSelectedOption] = useState('itemWise');
    const [customerOptions, setCustomerOptions] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customerId, setCustomer] = useState('0');
    const { isAuthenticated, companyId, branchId, permissions, token, logout } = useAuth();


    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
        fetchCustomerData();
    }, [isAuthenticated, companyId, branchId, token, logout]);

    useEffect(() => {
        if (fromDate && toDate) {
            fetchTableData();
        }
    }, [fromDate, toDate]);

    const totals = useMemo(() => {
        return dataItem.reduce((acc, item) => ({
            totalopbal: acc.totalopbal + item.OpeningQty,
            totalp: acc.totalp + item.PurchaseQty,
            totalprtn: acc.totalprtn + item.PurchaseReturnQty,
            totals: acc.totals + item.SaleQty,
            totalsrtn: acc.totalsrtn + item.SaleReturnQty,
            totalstin: acc.totalstin + item.StockInQty,
            totalstout: acc.totalstout + item.StockOutQty,
            totalrpl: acc.totalrpl + item.ReplaceQty,
            totalcls: acc.totalcls + item.ClosingQty,
            totalstkval: acc.totalstkval + item.StockValue,
            totalsalval: acc.totalsalval + item.SaleValue
        }), {
            totalopbal: 0,
            totalp: 0,
            totalprtn: 0,
            totals: 0,
            totalsrtn: 0,
            totalstin: 0,
            totalstout: 0,
            totalrpl: 0,
            totalcls: 0,
            totalstkval: 0,
            totalsalval: 0
        });
    }, [dataItem]);

    const fetchCustomerData = () => {

    };

    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    const getFormattedStartDate = () => {
        const monthStartDate = new Date();
        monthStartDate.setDate(1);
        return monthStartDate.toISOString().split('T')[0];
    };

    const handleFromDateChange = (e) => {
        const selectedDate = e.target.value;
        setFromDate(selectedDate);
    };

    const handleToDateChange = (e) => {
        const selectedDate = e.target.value;
        setToDate(selectedDate);
    };

    const hasReadReportPermission = permissions.includes('Read Reports');
    const [loading, setLoading] = useState(false);

    const fetchTableData = useCallback(async () => {
        if (hasReadReportPermission && !loading) {
            setLoading(true);
            try {
                const response = await fetchData('ReportApi', `/Stock/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
                if (response.responseCode === '1111') {
                    setDataItem(response.response_data);
                }
            } catch (error) {
                console.error('Error fetching Stock Details:', error);
            }
            finally {
                setLoading(false);
            }
        }
    }, [hasReadReportPermission, loading, companyId, branchId, fromDate, toDate, token]);

    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    const toggleSearchInput = () => {
        setShowSearchInput(!showSearchInput);
    };

    const filteredDataItem = useMemo(() => {
        if (!searchQuery) return dataItem;
        const query = searchQuery.toLowerCase();
        return dataItem.filter((item) =>
            Object.values(item).some((value) =>
                String(value).toLowerCase().includes(query)
            )
        );
    }, [dataItem, searchQuery]);

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        return new Date(dateString).toLocaleDateString('en-GB', options);
    };

   

    const handleExportExcel = () => {
        if (!filteredDataItem || filteredDataItem.length === 0) {
            console.warn("No data to export");
            return;
        }

        const categoryDataMap = {};
        filteredDataItem.forEach((rowData) => {
            let category = rowData.CategoryName ? rowData.CategoryName.trim() : "Unnamed Category";
            const key = category.toLowerCase();
            if (!categoryDataMap[key]) {
                categoryDataMap[key] = { name: category, rows: [] };
            }
            categoryDataMap[key].rows.push(rowData);
        });

        const workbook = new ExcelJS.Workbook();

        const headerRowValues = [
            'Code', 'Item', 'Opening', 'Purchase', 'Purchase Return', 'Sale', 'Sale Return', 'Stock In', 'Stock Out',
            'Replacement', 'Closing', 'MRP', 'Sale Value', 'GST%', 'HSN Code',
            'PG', 'IG', 'ISG', 'PF', 'Disc%', 'Category', 'Group', 'Season Code', 'Size Code'
        ];

        const rightAlignColumns = [
            'Opening', 'Purchase', 'Purchase Return', 'Sale', 'Sale Return', 'Stock In', 'Stock Out',
            'Replacement', 'Closing', 'Purchase Rate', 'MRP', 'Stock Value', 'Sale Value', 'GST%', 'Disc%'
        ];

        const safeSheetName = (name) => name.replace(/[:\\/?*\[\]]/g, "").substring(0, 31) || "Sheet";

        const addSheetWithData = (sheetName, caption, dataRows, totalRowArray) => {
            const worksheet = workbook.addWorksheet(safeSheetName(sheetName));

            // Caption row
            worksheet.mergeCells(1, 1, 1, headerRowValues.length);
            const captionCell = worksheet.getCell(1, 1);
            captionCell.value = caption;
            captionCell.font = { size: 14, bold: true };
            captionCell.alignment = { vertical: 'middle', horizontal: 'left' };

            // Header
            const headerRow = worksheet.addRow(headerRowValues);
            headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4F81BD' } };
            headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
            worksheet.views = [{ state: 'frozen', ySplit: 2 }];

            // Data rows
            dataRows.forEach(row => {
                const addedRow = worksheet.addRow(row);
                addedRow.eachCell((cell, colNumber) => {
                    if (rightAlignColumns.includes(headerRowValues[colNumber - 1])) {
                        cell.alignment = { horizontal: 'right' };
                    }
                });
            });

            // Total row
            const totalRow = worksheet.addRow(totalRowArray);
            totalRow.eachCell((cell) => {
                cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
                cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF4CAF50" } };
            });

            worksheet.columns = headerRowValues.map(() => ({ width: 15 }));
        };

        // All Data
        const allDataRows = filteredDataItem.map(rowData => [
            parseFloat(rowData.Code),
            rowData.Name,
            rowData.OpeningQty,
            rowData.PurchaseQty,
            rowData.PurchaseReturnQty,
            rowData.SaleQty,
            rowData.SaleReturnQty,
            rowData.StockInQty,
            rowData.StockOutQty,
            rowData.ReplaceQty,
            rowData.ClosingQty,
            // parseFloat(rowData.PurchaseRate.toFixed(2)),
            parseFloat(rowData.MRP.toFixed(2)),
            // parseFloat(rowData.StockValue.toFixed(2)),
            parseFloat(rowData.SaleValue.toFixed(2)),
            rowData.GSTRate,
            rowData.HSNCode,
            rowData.PG,
            rowData.IG,
            rowData.ISG,
            rowData.PF,
            rowData.DiscountPercentage,
            rowData.CategoryName,
            rowData.ItemGroupName,
            rowData.SeasonCode,
            rowData.SizeCode
        ]);

        const totalRowAll = ['', 'Total',
            filteredDataItem.reduce((a, b) => a + b.OpeningQty, 0),
            filteredDataItem.reduce((a, b) => a + b.PurchaseQty, 0),
            filteredDataItem.reduce((a, b) => a + b.PurchaseReturnQty, 0),
            filteredDataItem.reduce((a, b) => a + b.SaleQty, 0),
            filteredDataItem.reduce((a, b) => a + b.SaleReturnQty, 0),
            filteredDataItem.reduce((a, b) => a + b.StockInQty, 0),
            filteredDataItem.reduce((a, b) => a + b.StockOutQty, 0),
            filteredDataItem.reduce((a, b) => a + b.ReplaceQty, 0),
            filteredDataItem.reduce((a, b) => a + b.ClosingQty, 0),
            '', 
            // filteredDataItem.reduce((a, b) => a + parseFloat(b.StockValue.toFixed(2)), 0),
            filteredDataItem.reduce((a, b) => a + parseFloat(b.SaleValue.toFixed(2)), 0),
            '', '', '', '', '', '', '', '', ''
        ];

        addSheetWithData(
            "All Data",
            `Consolidated Stock From: ${formatDate(fromDate)} To: ${formatDate(toDate)}`,
            allDataRows,
            totalRowAll
        );

        // Per Category
        Object.values(categoryDataMap).forEach(({ name, rows }) => {
            const categoryRows = rows.map(rowData => [
                parseFloat(rowData.Code),
                rowData.Name,
                rowData.OpeningQty,
                rowData.PurchaseQty,
                rowData.PurchaseReturnQty,
                rowData.SaleQty,
                rowData.SaleReturnQty,
                rowData.StockInQty,
                rowData.StockOutQty,
                rowData.ReplaceQty,
                rowData.ClosingQty,
                // parseFloat(rowData.PurchaseRate.toFixed(2)),
                parseFloat(rowData.MRP.toFixed(2)),
                // parseFloat(rowData.StockValue.toFixed(2)),
                parseFloat(rowData.SaleValue.toFixed(2)),
                rowData.GSTRate,
                rowData.HSNCode,
                rowData.PG,
                rowData.IG,
                rowData.ISG,
                rowData.PF,
                rowData.DiscountPercentage,
                rowData.CategoryName,
                rowData.ItemGroupName,
                rowData.SeasonCode,
                rowData.SizeCode
            ]);

            const totalRowCategory = ['', 'Total',
                rows.reduce((a, b) => a + b.OpeningQty, 0),
                rows.reduce((a, b) => a + b.PurchaseQty, 0),
                rows.reduce((a, b) => a + b.PurchaseReturnQty, 0),
                rows.reduce((a, b) => a + b.SaleQty, 0),
                rows.reduce((a, b) => a + b.SaleReturnQty, 0),
                rows.reduce((a, b) => a + b.StockInQty, 0),
                rows.reduce((a, b) => a + b.StockOutQty, 0),
                rows.reduce((a, b) => a + b.ReplaceQty, 0),
                rows.reduce((a, b) => a + b.ClosingQty, 0),
                '', 
                // rows.reduce((a, b) => a + parseFloat(b.StockValue.toFixed(2)), 0),
                rows.reduce((a, b) => a + parseFloat(b.SaleValue.toFixed(2)), 0),
                '', '', '', '', '', '', '', '', ''
            ];

            addSheetWithData(
                name,
                `Consolidated Stock For Category: ${name} From: ${formatDate(fromDate)} To: ${formatDate(toDate)}`,
                categoryRows,
                totalRowCategory
            );
        });

        // Save file
        workbook.xlsx.writeBuffer().then((buffer) => {
            saveAs(new Blob([buffer]), `Consolidated_Stock_${formatDate(fromDate)}_${formatDate(toDate)}.xlsx`);
        });
    };




    const handlePrint = () => {

    };

    return (

        <div className="w-full  mx-auto" style={{ width: '95%', marginTop: '10px' }}>
            <div style={{ width: '100%', marginBottom: '2', display: 'flex', flexWrap: 'nowrap', alignItems: 'center' }}>
                <div className='w-full mb-2 flex' style={{ marginTop: '10px' }}>
                    <h6 className="block uppercase tracking-wide text-black productHead flex-grow linkcolor" style={{ fontFamily: 'Georgia, Cambria, "Times New Roman", Times, serif', color: 'black' }}>
                        Consolidated STOCK
                    </h6>
                </div>
                <div className='w-full mb-2 flex' style={{ marginTop: '10px' }}>
                    {loading && (
                        <div style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: 9999
                        }}>
                            <div>
                                <CircularProgress style={{ color: 'white' }} />
                                <span style={{ color: 'white', marginTop: '10px', display: 'block' }}>Loading...</span>
                            </div>
                        </div>
                    )}
                    <Button
                        style={{ color: "black", height: '36px', marginLeft: '10px' }}
                        onClick={fetchTableData}
                        disabled={loading}
                        className="linkcolor"
                    >
                        <IconButton onClick={fetchTableData} disabled={loading}>
                            <RefreshIcon style={{ color: loading ? 'grey' : 'brown' }} />
                        </IconButton>
                        <span style={{ color: loading ? 'grey' : 'brown', fontWeight: 'bolder' }}>
                            {loading ? 'Loading...' : 'Refresh'}
                        </span>
                    </Button>
                    <Button
                        style={{ color: "black", height: '36px', marginLeft: '10px' }}
                        onClick={handleExportExcel}
                        className="linkcolor"
                    >
                        <IconButton onClick={handleExportExcel}>
                            <FeedIcon style={{ color: 'darkgreen' }} />
                        </IconButton>
                        <span style={{ color: 'darkgreen', fontWeight: 'bolder' }}>Excel</span>
                    </Button>

                    <div style={{ marginBottom: '10px', marginLeft: '20px', display: 'flex', alignItems: 'center' }}>
                        <SearchIcon onClick={toggleSearchInput} style={{ cursor: 'pointer' }} />
                        {showSearchInput && (
                            <input className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                type="text"
                                placeholder="Search..."
                                value={searchQuery}
                                onChange={handleSearch}
                                style={{ marginLeft: '10px' }}
                            />
                        )}
                    </div>
                </div>
                <div className="watermark">
                    <CopyrightIcon /><b style={{ fontFamily: 'Script Bold Italic' }}>EFFTABS SOLUTIONS LLP</b>
                </div>

                <div style={{ marginRight: '2', maxWidth: '150px', marginLeft: '10px' }}>
                    <label className="block uppercase tracking-wide text-black text-xs font-bold mb-2" htmlFor="gstNumber">
                        FromDate
                    </label>
                    <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                        type="date" value={fromDate} onChange={handleFromDateChange}
                        style={{ marginTop: '-10px', shadow: 'appearance-none', border: 'rounded', width: '100%', paddingY: '2', paddingX: '3', color: 'gray-700', leadingTight: true, focus: { color: 'gray-600', shadow: 'outline' } }}
                    />
                </div>
                <div style={{ marginRight: '2', maxWidth: '150px', marginLeft: '10px' }}>
                    <label className="block uppercase tracking-wide text-black text-xs font-bold mb-2" htmlFor="gstNumber">
                        ToDate
                    </label>
                    <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                        type="date" value={toDate} onChange={handleToDateChange}
                        style={{ marginTop: '-10px', shadow: 'appearance-none', border: 'rounded', width: '100%', paddingY: '2', paddingX: '3', color: 'gray-700', leadingTight: true, focus: { color: 'gray-600', shadow: 'outline' } }}
                    />
                </div>
            </div>

            {/* <div className='w-full mb-5 flex' style={{ maxHeight: '470px', overflowY: 'auto', Width: '2500px' }}> */}
            <div className='w-full mb-5 flex' style={{ maxHeight: '550px', overflowY: 'auto', marginTop: '7px' }}>
                <TableContainer component={Paper}>
                    <Table stickyHeader>
                        <TableHead sx={{ backgroundColor: '#08396B', Width: '2500px' }}>
                            <TableRow sx={{ whiteSpace: 'nowrap' }}>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Code</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Item</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Opening</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Purchase</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Purchase Return</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Sale</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Sale Return</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Stock In</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Stock Out</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Replacement</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Closing</TableCell>
                                {/* <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Purchase Rate</TableCell> */}
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>MRP</TableCell>
                                {/* <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Stock Value</TableCell> */}
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Sale Value </TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>GST%</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>HSN Code</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>PG</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>IG</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>ISG</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>PF</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Disc%</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Category</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Group</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Season Code</TableCell>
                                <TableCell sx={{ backgroundColor: '#008080', borderBottom: '1px solid #F5F5F5', fontWeight: 'bolder', fontSize: '14px', padding: '7px', color: 'whitesmoke', textTransform: 'uppercase' }}>Size Code</TableCell>
                            </TableRow>
                        </TableHead>


                        <TableBody sx={{ backgroundColor: 'whitesmoke' }}>
                            {filteredDataItem.map((data, index) => (
                                <TableRow
                                    key={data.id}
                                    className={'highlighted-row'}
                                    sx={{
                                        padding: '5px',
                                        height: '52px',

                                        backgroundColor: 'transparent',

                                    }}
                                >
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px' }}>{data.Code}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px' }}>{data.Name}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px', textAlign: 'right' }}>{data.OpeningQty}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px', textAlign: 'right' }}>{data.PurchaseQty}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px', textAlign: 'right' }}>{data.PurchaseReturnQty}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px', textAlign: 'right' }}>{data.SaleQty}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px', textAlign: 'right' }}>{data.SaleReturnQty}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px', textAlign: 'right' }}>{data.StockInQty}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px', textAlign: 'right' }}>{data.StockOutQty}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px', textAlign: 'right' }}>{data.ReplaceQty}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px', textAlign: 'right' }}>{data.ClosingQty}</TableCell>
                                    {/* <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px', textAlign: 'right' }}>{data.PurchaseRate.toFixed(2)}</TableCell> */}
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px', textAlign: 'right' }}>{data.MRP.toFixed(2)}</TableCell>
                                    {/* <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px', textAlign: 'right' }}>{data.StockValue.toFixed(2)}</TableCell> */}
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px', textAlign: 'right' }}>{data.SaleValue.toFixed(2)}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px', textAlign: 'right' }}>{data.GSTRate}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px' }}>{data.HSNCode}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px' }}>{data.PG}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px' }}>{data.IG}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px' }}>{data.ISG}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px' }}>{data.PF}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px' }}>{data.DiscountPercentage}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px' }}>{data.CategoryName}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px' }}>{data.ItemGroupName}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px' }}>{data.SeasonCode}</TableCell>
                                    <TableCell sx={{ borderBottom: '1px solid black', borderRight: '1px solid grey', color: 'black ', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px' }}>{data.SizeCode}</TableCell>
                                </TableRow>
                            ))}

                            <TableRow
                                className={'highlighted-row'}
                                sx={{
                                    padding: '7px',
                                    height: '52px',

                                    backgroundColor: '#336D82',

                                }}
                            >
                                <TableCell colSpan={2} />
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', borderRight: '1px solid grey', color: 'whitesmoke', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px', textAlign: 'right' }}>{totals.totalopbal}</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', borderRight: '1px solid grey', color: 'whitesmoke', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px', textAlign: 'right' }}>{totals.totalp}</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', borderRight: '1px solid grey', color: 'whitesmoke', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px', textAlign: 'right' }}>{totals.totalprtn}</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', borderRight: '1px solid grey', color: 'whitesmoke', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px', textAlign: 'right' }}>{totals.totals}</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', borderRight: '1px solid grey', color: 'whitesmoke', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px', textAlign: 'right' }}>{totals.totalsrtn}</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', borderRight: '1px solid grey', color: 'whitesmoke', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px', textAlign: 'right' }}>{totals.totalstin}</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', borderRight: '1px solid grey', color: 'whitesmoke', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px', textAlign: 'right' }}>{totals.totalstout}</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', borderRight: '1px solid grey', color: 'whitesmoke', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px', textAlign: 'right' }}>{totals.totalrpl}</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', borderRight: '1px solid grey', color: 'whitesmoke', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px', textAlign: 'right' }}>{totals.totalcls}</TableCell>
                                <TableCell colSpan={1} />
                                {/* <TableCell sx={{ borderBottom: '1px solid #F5F5F5', borderRight: '1px solid grey', color: 'whitesmoke', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px', textAlign: 'right' }}>{totals.totalstkval.toFixed(2)}</TableCell> */}
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', borderRight: '1px solid grey', color: 'whitesmoke', fontWeight: 'bolder', padding: '5px', textAlign: 'left', fontSize: '12px', textAlign: 'right' }}>{totals.totalsalval.toFixed(2)}</TableCell>
                                <TableCell colSpan={11} />
                            </TableRow>

                        </TableBody>
                    </Table>
                </TableContainer>
            </div>
        </div>
    );
}

export default Stock;
